import type { License, LicenseListQuery, LicenseStatus } from '@central/api';
import { Card, PageHeader, StatusChip, formatDate } from '@central/ui';
import { Button, Select, Table, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import { licenseStatusChip, mono } from '../lib/labels';
import {
  activationsText,
  expiringChoices,
  licenseStatuses,
  type LicensesSearch,
} from '../lib/license';
import { useCanWrite } from '../lib/me';
import { NewLicenseModal } from './CustomerDialogs';

/**
 * Every license across customers, soonest expiry first. Filters live in the
 * URL like the customer list and the audit log.
 */
export function Licenses() {
  const api = useApi();
  const navigate = useNavigate({ from: '/licenses' });
  const search = useSearch({ from: '/app/licenses' });
  const canWrite = useCanWrite();
  const [text, setText] = useState(search.q ?? '');
  const [query] = useDebouncedValue(text.trim(), 250);

  useEffect(() => {
    if ((search.q ?? '') !== query)
      void navigate({ search: (s) => ({ ...s, q: query || undefined }), replace: true });
  }, [query, search.q, navigate]);

  const filter: LicenseListQuery = {
    query: query || undefined,
    customerId: search.customerId,
    status: search.status,
    expiringWithinDays: search.expiring,
  };
  const list = useInfiniteQuery({
    queryKey: keys.licenseSearch(filter),
    queryFn: ({ pageParam }) => api.licenses.list({ ...filter, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = !!(query || search.customerId || search.status || search.expiring);
  const apply = (next: Partial<LicensesSearch>) =>
    void navigate({ search: (s) => ({ ...s, ...next }), replace: true });

  return (
    <>
      <PageHeader
        title="Licenses"
        actions={
          canWrite && (
            <Button color="dark" size="sm" h={40} onClick={() => apply({ new: true })}>
              New license
            </Button>
          )
        }
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <TextInput
          aria-label="Search licenses"
          placeholder="License key or customer…"
          size="sm"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <CustomerSelect
          label="Customer"
          placeholder="Any customer"
          value={search.customerId ?? ''}
          onChange={(id) => apply({ customerId: id || undefined })}
        />
        <Select
          label="Status"
          size="sm"
          value={search.status ?? ''}
          onChange={(v) => apply({ status: (v || undefined) as LicenseStatus | undefined })}
          data={[
            { value: '', label: 'Any status' },
            ...licenseStatuses.map((s) => ({ value: s, label: licenseStatusChip[s].label })),
          ]}
          allowDeselect={false}
        />
        <Select
          label="Expires"
          size="sm"
          value={search.expiring ? String(search.expiring) : ''}
          onChange={(v) => apply({ expiring: v ? Number(v) : undefined })}
          data={[
            { value: '', label: 'Any time' },
            ...expiringChoices.map((n) => ({ value: String(n), label: `Within ${n} days` })),
          ]}
          allowDeselect={false}
        />
      </div>
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            items.length === 0 ? (
              <Empty>{filtered ? 'No licenses match.' : 'No licenses yet.'}</Empty>
            ) : (
              <DataTable
                label="Licenses"
                minWidth={820}
                head={['Key', 'Customer', 'Status', 'Starts', 'Expires', 'Activations']}
              >
                {items.map((l) => (
                  <LicenseRow key={l.id} l={l} />
                ))}
              </DataTable>
            )
          }
        </QueryState>
        {list.hasNextPage && (
          <LoadMore
            what="licenses"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          />
        )}
      </Card>
      {search.new && canWrite && (
        <NewLicenseModal opened onClose={() => apply({ new: undefined })} />
      )}
    </>
  );
}

function LicenseRow({ l }: { l: License }) {
  const chip = licenseStatusChip[l.status];
  return (
    <Table.Tr>
      <Table.Td style={{ ...mono, wordBreak: 'break-all' }}>
        <Link to="/licenses/$licenseId" params={{ licenseId: l.id }}>
          {l.key}
        </Link>
      </Table.Td>
      <Table.Td style={{ fontWeight: 600 }}>
        <Link to="/customers/$customerId" params={{ customerId: l.customerId }}>
          {l.customerName || l.customerId}
        </Link>
      </Table.Td>
      <Table.Td>
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
      </Table.Td>
      <Table.Td>{formatDate(l.startsAt)}</Table.Td>
      <Table.Td>{formatDate(l.expiresAt)}</Table.Td>
      <Table.Td style={mono}>
        {l.activeActivations} of {activationsText(l.allowedActivations)}
      </Table.Td>
    </Table.Tr>
  );
}
